// ** React Imports
import { useState } from "react";

// ** Redux & Store & Actions
import { useSelector } from "react-redux";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import { toast } from "helpers/toastWrapper";

// ** Custom Components
import ProgressBar from "containers/Landing/containers/Register/components/ProgressBar/ProgressBar";
import Block from "components/layout/Block";
import Text from "components/typography/Text";
import { ButtonLanding } from "components/layout/Button/ButtonLanding";
import ErrorMessage from "containers/Landing/components/ErrorMessage";
import StepLabel from "../../Register/components/StepLabel";
import SendRegistrationEmailButton from "containers/Landing/components/SendRegistrationEmailButton";

// ** Custom Hooks
import useUpdateEffect from "hooks/useUpdateEffect";

// ** Utils & Helpers
import api from "helpers/ApiClient";
import { email as validateEmail } from "utils/validation";

// ** Styles Import
import { StyledAuthPanelInput } from "containers/Landing/styles/AuthPanelInput.styled";

// #####################################################

const Step1 = ({
	currentStep,
	setCurrentStep,
	setEmailRecover,
	isModal = false,
}) => {
	const { t } = useTranslation("pages", "landing");

	const language = useSelector((state) => state.globalCommon.language);

	const [recoverStep1Loading, setRecoverStep1Loading] = useState(false);
	const [email, setEmail] = useState("");
	const [emailEmptyError, setEmailEmptyError] = useState(false);
	const [emailInvalidError, setEmailInvalidError] = useState(false);
	const [emailApiError, setEmailApiError] = useState("");
	const [
		showSendRegistrationEmailButton,
		setShowSendRegistrationEmailButton,
	] = useState(false);
	const [errorStatusCode, setErrorStatusCode] = useState(1000);

	const handleSubmit = (event) => {
		event.preventDefault();
		setRecoverStep1Loading(true);

		if (email === "") {
			setEmailEmptyError(true);
			setRecoverStep1Loading(false);
			return;
		}

		if (!validateEmail(email)) {
			setEmailInvalidError(true);
			setRecoverStep1Loading(false);
			return;
		}
		setEmailInvalidError(false);

		if (!emailEmptyError && !emailInvalidError) {
			api.post("/authorization/remember/web", { email })
				.then(() => {
					setEmailRecover(email);
					setCurrentStep("second");
				})
				.catch((err) => {
					if (err.response.data.code >= 1000) {
						setErrorStatusCode(err.response.data.code);
						setShowSendRegistrationEmailButton(true);
					}
					toast.error(err.response.data.error);
				})
				.finally(() => {
					setRecoverStep1Loading(false);
				});
		} else {
			setRecoverStep1Loading(false);
		}
	};

	// ** Efekt ponownie sprawdza dostępność email po zmianie języka,
	// ** w celu pobrania z API komunikatu błędu w odpowiednim języku
	useUpdateEffect(() => {
		if (emailApiError) {
			api.post("/authorization/remember/web", { email })
				.then(() => {
					setCurrentStep("second");
				})
				.catch((err) => {
					toast.error(err.response.data.error);
				})
				.finally(() => {
					setRecoverStep1Loading(false);
				});
		}
	}, [language]);

	// ** A/B Tests
	const authModalVersion = useSelector(
		(state) => state.globalCommon.authModal.version
	);

	// #####################################################

	return (
		<Block>
			<StepLabel
				label={t("landing:recover.stepDescriptions.header")}
				isModal={isModal}
				authModalVersion={authModalVersion}
			/>

			<form onSubmit={handleSubmit}>
				<Block mb={2}>
					<StyledAuthPanelInput
						label="email"
						id="email"
						value={email}
						onChange={(event) => {
							setEmail(event.target.value.trim());
							setEmailApiError("");

							if (
								emailInvalidError &&
								event.target.value === ""
							) {
								setEmailInvalidError(false);
							} else if (emailInvalidError) {
								setEmailInvalidError(
									!validateEmail(event.target.value)
								);
							}
							setEmailEmptyError(event.target.value === "");
						}}
						onBlur={(event) => {
							setEmailInvalidError(
								event.target.value !== "" &&
									!validateEmail(event.target.value)
							);
							setEmailEmptyError(event.target.value === "");

							const check =
								event?.nativeEvent?.relatedTarget?.classList?.contains(
									"PasswordRecoverButton"
								);

							if (
								event.target.value !== "" &&
								validateEmail(event.target.value) &&
								!check
							) {
								api.post(
									"/authorization/remember/valid/email",
									{
										email: event.target.value,
									}
								)
									.then(() => {
										setEmailApiError("");
									})
									.catch((err) => {
										if (err?.response?.data?.error) {
											setEmailApiError(
												err.response.data.error
											);
										}
									});
							}
						}}
						type="text"
						autoComplete="on"
						active={Boolean(email)}
						placeholder={t("landing:register.emailPlaceholder")}
						register
						withBoxShadow
					/>
					{emailEmptyError && (
						<ErrorMessage>
							{t("landing:recover:emailEmptyError")}
						</ErrorMessage>
					)}
					{emailInvalidError && (
						<ErrorMessage>
							{t("landing:recover:emailInvalidError")}
						</ErrorMessage>
					)}

					{emailApiError && (
						<ErrorMessage>{emailApiError}</ErrorMessage>
					)}
				</Block>

				<Block mt={3}>
					<Text
						color={
							isModal && authModalVersion === "darkAndLight"
								? "black"
								: "whiteAlways"
						}
					>
						{t(`landing:recover.stepDescriptions.${currentStep}`)}
					</Text>
				</Block>

				{showSendRegistrationEmailButton && email && (
					<SendRegistrationEmailButton
						email={email}
						remainingTime={errorStatusCode - 1000}
						isModal={isModal}
					/>
				)}

				{/* Pasek Postępu */}
				<ProgressBar currentStep={1} totalSteps={4} />

				<ButtonLanding
					className="PasswordRecoverButton"
					disabled={recoverStep1Loading}
					type="submit"
				>
					{t("landing:recover.action")}
				</ButtonLanding>
			</form>
		</Block>
	);
};

// #####################################################

export default Step1;

// #####################################################
